// Contracts for slots (all five slot games share one HTTP API).
//   POST /api/slots/:game/spin   body SlotSpinInput  → SlotSpinResponse
//   GET  /api/slots/:game/state  ?bet=<level>        → SlotStateResponse
// `result` / `state` are the engine types from `@tg/games/slots/engine` (SpinResult / SlotState):
// the client plugs them in via the generic parameters.
import { z } from 'zod';
import type { FairProof } from '../fair';
import { SLOT_IDS, zIdempotencyKey } from '../common';

export const zSlotId = z.enum(SLOT_IDS);

export const zSlotSpinInput = z.object({
  /** bet level (must be one of the game's `betLevels`); ignored while a feature is active */
  bet: z.number().int().positive().max(1_000_000),
  /** 'buy' = buy bonus (Spaceport) */
  action: z.enum(['spin', 'buy']).default('spin'),
  idempotencyKey: zIdempotencyKey,
});
export type SlotSpinInput = z.input<typeof zSlotSpinInput>;

export const zSlotStateQuery = z.object({ bet: z.coerce.number().int().positive().optional() });

export interface SlotSpinResponse<Result = unknown, State = unknown> {
  betId: string;
  result: Result;
  /** tokens charged by this spin (0 for a free spin / respin) */
  cost: number;
  /** tokens credited by this spin */
  payout: number;
  /** balance after the spin */
  balance: number;
  /** state after the spin (active feature, progress) */
  state: State;
  /** fair data of this spin (serverSeed revealed after rotation) */
  fair: FairProof;
  /** true: the idempotencyKey was already used — this is the original response */
  duplicate: boolean;
}

export interface SlotStateResponse<State = unknown> {
  game: string;
  betLevels: number[];
  /** bet level this state belongs to (0 = not bet-dependent) */
  betLevel: number;
  state: State;
  /**
   * Bet-scoped games only (Workshop): the saved state of EVERY bet level the player has played,
   * so the client can show the progress of each bet (no hidden progress "trap").
   */
  levels?: { betLevel: number; state: State }[];
  /** hash of the active server seed + client seed + next nonce */
  fair: { serverSeedHash: string; clientSeed: string; nonce: number };
}
